import React, { useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { formatDateInput, isValidDate, parseISODateToLocal } from '../utils/time';

interface DateInputProps {
    value: string;
    onChange: (isoDate: string) => void;
    placeholder?: string;
    className?: string;
    wrapperClassName?: string;
    label?: string;
    required?: boolean;
}

const DateInput: React.FC<DateInputProps> = ({
    value,
    onChange,
    placeholder = 'MM/dd/yyyy',
    className = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
    wrapperClassName = 'w-full',
    label,
    required = false
}) => {
    const handleDateInputChange = useCallback(
        (e: any) => {
            if (!e || e.type === 'click') return;

            const target = e.target as HTMLInputElement;
            const currentValue = target.value;

            if (e.type === 'change' || e.type === 'input' || e.type === 'keyup') {
                const numbers = currentValue.replace(/\D/g, '');

                setTimeout(() => {
                    const formatted = formatDateInput(currentValue);
                    if (target.value !== formatted) {
                        target.value = formatted;
                    }

                    if (numbers.length === 8) {
                        const month = parseInt(numbers.slice(0, 2), 10);
                        const day = parseInt(numbers.slice(2, 4), 10);
                        const year = parseInt(numbers.slice(4, 8), 10);

                        if (isValidDate(month, day, year)) {
                            const date = new Date(Date.UTC(year, month - 1, day));
                            if (!isNaN(date.getTime())) {
                                onChange(date.toISOString().slice(0, 10));
                            }
                        }
                    }
                }, 0);
            }
        },
        [onChange]
    );

    return (
        <div>
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    {label} {required && '*'}
                </label>
            )}
            <DatePicker
                selected={parseISODateToLocal(value)}
                onChange={(d: Date | null) => {
                    if (d) {
                        const iso = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
                            .toISOString()
                            .slice(0, 10);
                        onChange(iso);
                    }
                }}
                onChangeRaw={handleDateInputChange}
                dateFormat="MM/dd/yyyy"
                placeholderText={placeholder}
                className={className}
                wrapperClassName={wrapperClassName}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
            />
        </div>
    );
};

export default DateInput;
